import { useEffect, useState } from "react";
import { Switch, Route } from "react-router";
import AppWrapper from "../containers/appWrapper";
import DesktopView from "../components/desktopView/desktopView";
import { GrowthBookProvider, useFeatureIsOn } from "@growthbook/growthbook-react";
import { growthbook } from "../common/constants";
import ErrorScreen from "../components/common/errorScreen";
import RotatePhone from "../assets/images/rotate-icon.svg";
import { RouteConfig } from "./routeConfigs";
import { MetaTags } from "../components/metaTag";

export const RootRouteComponentWithSubRoutes = ({ routes }: { routes: RouteConfig[] }) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(false);

  const initGrowthbook = async () => {
    await growthbook.init(); // Load features asynchronously
  };

  useEffect(() => {
    initGrowthbook();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
      setIsLandscapeMobile(
        window.innerWidth <= 1024 && window.innerWidth > window.innerHeight && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      );
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check if the current route is marked as desktop-friendly
  const isDesktopFriendlyRoute = () => {
    const pathname = window.location.pathname;

    // Recursive function to check if a route or any of its nested routes is desktop-friendly
    const isRouteDesktopFriendly = (routeList: RouteConfig[]): boolean => {
      if (!routeList || routeList.length === 0) return false;

      return routeList.some((route) => {
        // Skip if route doesn't have a path
        if (!route.path) return false;

        const routePath = route.path.toString();

        // Check if this route matches the current path
        const isRouteMatch = route.exact ? pathname === routePath : pathname.startsWith(routePath);

        // If this route matches and is desktop friendly, return true
        if (isRouteMatch && route.desktopFriendly) {
          return true;
        }

        // If route has nested routes, check them recursively
        if (route.routes && route.routes.length > 0) {
          return isRouteDesktopFriendly(route.routes);
        }

        return false;
      });
    };

    // Start checking from the top-level routes
    return isRouteDesktopFriendly(routes);
  };

  if (isDesktop && !isDesktopFriendlyRoute()) {
    return <DesktopView />;
  } else if (isLandscapeMobile) {
    return (
      <div className="font-primary text-[22px] min-h-screen bg-black fixed inset-0 bg-opacity-90 z-50 flex justify-center items-center text-white content-center ">
        <div>
          <img src={RotatePhone} alt={RotatePhone} className="w-16 h-16 mx-20" />
          <p className="font-primary text-[22px]">Please turn your device</p>
        </div>
      </div>
    );
  } else {
    return (
      <Switch>
        {routes.map((route: any) => {
          const { key, ...rest } = route;
          return <RouteWithSubRoutes key={key} {...rest} />;
        })}
        <Route component={ErrorScreen} />
      </Switch>
    );
  }
};

// Wrap Routes in AppWrapper (Can create any wrapper based on different requirement/layout like login, faq, etc)
export const RootAppComponentWithRoutes = ({ routes }: { routes: any }) => (
  <GrowthBookProvider growthbook={growthbook}>
    <AppWrapper>
      <RootRouteComponentWithSubRoutes routes={routes} />
    </AppWrapper>
  </GrowthBookProvider>
);

// Allows for rendering nested Route components
export const RouteWithSubRoutes = (route: any) => {
  // Use GrowthBook feature flag check
  const isFeatureEnabled = route.featureFlag ? useFeatureIsOn(route.featureFlag) : true;

  const ComponentToRender = isFeatureEnabled ? route.component : route.redirectComponent;

  return (
    <Route
      path={route.path}
      exact={route.exact}
      render={(props) => (
        <>
          <MetaTags />
          <ComponentToRender {...props} routes={route.routes} desktopFriendly={route.desktopFriendly} />
        </>
      )}
    />
  );
};
